import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { DesktopOnlyButton as DesktopOnlyButtonBase } from 'pages/Common/styles/ControlBar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mcpServerListQuery, mcpServerStepOptionsQuery } from 'query/mcp';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import MCPServerService from 'services/MCPServerService';
import { DeleteServerModal } from '../Servers/DeleteServerModal';
import { useMCPServerDetailPage } from './context';
import type { ReactNode } from 'react';

const DesktopOnlyButton = DesktopOnlyButtonBase as React.ComponentType<{
  color?: string;
  onClick?: () => void;
  'data-qa-id'?: string;
  children?: ReactNode;
  loading?: boolean;
}>;

const SERVER_LIST_QUERY_KEY = mcpServerListQuery().queryKey;
const STEP_OPTIONS_QUERY_KEY = mcpServerStepOptionsQuery().queryKey;

export const DeleteServerButton = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { server, canDelete } = useMCPServerDetailPage();

  const { mutateAsync: deleteServerMutation } = useMutation({
    mutationFn: (id: string) => MCPServerService.deleteMCPServer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SERVER_LIST_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: STEP_OPTIONS_QUERY_KEY,
      });
    },
  });

  const handleDeleteConfirmed = useCallback(async () => {
    if (!server) return;
    try {
      await deleteServerMutation(server.id);
      setShowDeleteModal(false);
      history.push('/mcp-servers');
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to delete MCP server'),
        variant: toastVariant.FAILURE,
      });
    }
  }, [server, deleteServerMutation, history, showToast, t, toastVariant]);

  if (!canDelete || !server) return null;

  return (
    <>
      <DesktopOnlyButton
        color="red"
        onClick={() => setShowDeleteModal(true)}
        data-qa-id="mcp-server-delete-button"
      >
        {t('Delete')}
      </DesktopOnlyButton>

      {showDeleteModal ? (
        <DeleteServerModal
          server={server}
          onConfirm={handleDeleteConfirmed}
          onHide={() => setShowDeleteModal(false)}
        />
      ) : null}
    </>
  );
};
